import * as yup from 'yup';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { CaseType, EntityType, Priority } from '~/common/enums';
import { AppContext } from '@/shared/context/context';
import type { AutocompleteOption } from '~/common/types';
import { BuildingRoleSelectorOption } from '@/shared/enums/BuildingRoleSelectorOption';
import { SELECTOR_CASES_QUERY_KEY } from '@/queries/cases/selectorCasesQuery';
import { type DefaultFormValues } from './FormValues';
import { replaceUrlParams } from '~/common/utils/utils';
import ROUTES from '@/containers/App/Router/Routes';
import { trpc } from '@/config/trpc';
import useCreateCase from '@/mutations/case/useCreateCase';
import { type UseToggle } from '@/shared/hooks/UseToggle';
import { valuesToCaseRequest } from '../utils';

type Props = {
  initialData?: DefaultFormValues;
  callback?: (res: string) => void;
  caseType?: CaseType;
  toggles: {
    openCaseInNewTab: UseToggle;
    pushNotification: UseToggle;
    redirect: UseToggle;
    showOnPortal: UseToggle;
  };
  toggle: UseToggle;
  templateId?: string;
};

const useAddCaseFormConfig = ({ initialData, callback, caseType, toggles, toggle, templateId }: Props) => {
  const { t } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { currentTeamUser, currentTeam } = useContext(AppContext);

  const onSuccess = (res: string) => {
    if (callback) {
      callback(res);
    }

    if (toggles.openCaseInNewTab.value) {
      const url = replaceUrlParams(ROUTES.CASE_DETAIL, {
        caseId: res,
      });

      window.open(url, '_blank');
    }

    if (toggles.redirect.value) {
      navigate(`/case/${res}`);
    }

    toggle.hide();
  };

  const { createCase, isCreatingCase } = useCreateCase({
    callback: onSuccess,
  });

  const { mutate: createSalesCase, isLoading: isCreatingSalesCase } = trpc.case.salesCases.create.useMutation({
    onSuccess: (res: string) => {
      queryClient.invalidateQueries(SELECTOR_CASES_QUERY_KEY);
      onSuccess(res);
    },
  });

  const handleSubmit = (values: DefaultFormValues) => {
    const baseRequest = valuesToCaseRequest(values);
    const createRequest = {
      ...baseRequest,
      caseTemplateId: templateId,
      rolesWithAccess:
        toggles.showOnPortal.value && currentTeam?.allowSyndicManagement
          ? Object.keys(BuildingRoleSelectorOption).filter(
            (opt) =>
              opt !== BuildingRoleSelectorOption.AllContactsAndCompanies &&
              opt !== BuildingRoleSelectorOption.None,
          )
          : null,
      sendPushNotification: toggles.pushNotification.value,
      showOnBuildingPortal: toggles.showOnPortal.value,
    };

    if (caseType === CaseType.Sales) {
      createSalesCase(createRequest);
    } else {
      createCase(createRequest);
    }
  };

  const initialValues = useMemo(
    () => ({
      administratorOptions: currentTeamUser
        ? [
          {
            data: {
              description: `${currentTeamUser.email} | ${currentTeamUser.role}`,
            },
            label: currentTeamUser.name ?? '',
            type: EntityType.User,
            value: currentTeamUser.id ?? '',
          },
        ]
        : [],
      admins: [],
      buildingOption: null,
      classification: null,
      clients: [],
      description: null,
      priority: Priority.Normal,
      requestor: null,
      ...initialData,
    }),
    [initialData, currentTeamUser],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape(
        {
          address: yup
            .object()
            .nullable()
            .when('buildingOption', {
              is: (val: AutocompleteOption) => !val,
              then: yup.object().nullable().required(t('fieldIsRequired')),
            }),
          administratorOptions: yup.array().min(1, t('fieldIsRequired')),
          buildingOption: yup
            .object()
            .nullable()
            .test('buildingOptionRequired', t('fieldIsRequired'), (value, schema) => {
              if (caseType === CaseType.Sales) {
                return !!value;
              }

              const { parent } = schema;
              const { address } = parent;
              if (!address) {
                return !!value;
              }
              return true;
            }),
          description: yup.string().nullable().required(t('fieldIsRequired')),
          requestor: yup.object().nullable().required(t('fieldIsRequired')),
        },
        [['address', 'buildingOption']],
      ),
    [caseType, t],
  );

  return {
    handleSubmit,
    initialValues,
    isCreatingCase: caseType === CaseType.Sales ? isCreatingSalesCase : isCreatingCase,
    validationSchema,
  };
};

export default useAddCaseFormConfig;
